import { z } from "zod";

import { TOUR_EVENT } from "./constants";

export const TOUR_STATUS = {
  NOT_SEEN: "NotSeen",
  SNOOZED: "Snoozed",
  IN_PROGRESS: "InProgress",
  SKIPPED: "Skipped",
  COMPLETED: "Completed",
} as const;

const MAX_HELP_BEACON_VISITS = 3;

const createTourStateSchema = (stepCount: number) =>
  z.object({
    status: z.enum(Object.values(TOUR_STATUS)),
    resumeStep: z.number().int().min(1).max(stepCount).nullable(),
    helpBeaconVisits: z.number().int().min(0).max(MAX_HELP_BEACON_VISITS),
  });

export type TourState = z.infer<ReturnType<typeof createTourStateSchema>>;

export const DEFAULT_TOUR_STATE: TourState = {
  status: TOUR_STATUS.NOT_SEEN,
  resumeStep: null,
  helpBeaconVisits: 0,
};

type TourEvent = (typeof TOUR_EVENT)[keyof typeof TOUR_EVENT];

export type TourEventBody =
  | { event: Exclude<TourEvent, typeof TOUR_EVENT.CLOSED> }
  | { event: typeof TOUR_EVENT.CLOSED; step: number };

export const parseTourState = (stored: unknown, stepCount: number): TourState =>
  createTourStateSchema(stepCount).catch(DEFAULT_TOUR_STATE).parse(stored);

export function applyTourEvent(state: TourState, body: TourEventBody, stepCount: number): TourState {
  if (state.status === TOUR_STATUS.COMPLETED) return state;

  switch (body.event) {
  case TOUR_EVENT.COMPLETED:
    return { ...state, status: TOUR_STATUS.COMPLETED, resumeStep: null };
  case TOUR_EVENT.SNOOZED:
    if (state.status !== TOUR_STATUS.NOT_SEEN) return state;
    return { ...state, status: TOUR_STATUS.SNOOZED };
  case TOUR_EVENT.CLOSED:
    if (body.step < 1 || body.step > stepCount) return state;
    return { ...state, status: TOUR_STATUS.IN_PROGRESS, resumeStep: body.step };
  case TOUR_EVENT.SKIPPED:
    return { ...state, status: TOUR_STATUS.SKIPPED, resumeStep: null };
  case TOUR_EVENT.HELP_BEACON_SEEN:
    if (state.status !== TOUR_STATUS.SNOOZED) return state;
    return {
      ...state,
      helpBeaconVisits: Math.min(state.helpBeaconVisits + 1, MAX_HELP_BEACON_VISITS),
    };
  }
}

export const deriveTourFlags = ({ status, resumeStep, helpBeaconVisits }: TourState) => ({
  showWelcome: status === TOUR_STATUS.NOT_SEEN,
  showPromo: status !== TOUR_STATUS.COMPLETED,
  showHelpBeacon: status === TOUR_STATUS.SNOOZED && helpBeaconVisits < MAX_HELP_BEACON_VISITS,
  resumeStep: status === TOUR_STATUS.IN_PROGRESS ? resumeStep : null,
});
